import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { exchangeWebHandoff, getMe } from "@/api/auth";
import { ApiError } from "@/api/client";
import {type AuthUser, useLandingAuth} from "@/state/authStore";

export type AuthBootstrapStatus =
  | "loading"
  | "unauthenticated"
  | "authenticated"
  | "error";

export function useAuthBootstrap() {
  const [searchParams, setSearchParams] = useSearchParams();
  const accessToken = useLandingAuth((s) => s.accessToken);
  const hydrated = useLandingAuth((s) => s.hydrated);
  const [status, setStatus] = useState<AuthBootstrapStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const exchangedHandoffRef = useRef<string | null>(null);

  useEffect(() => {
    const syncSession = (event: Event) => {
      const session = (event as CustomEvent<{
        accessToken: string;
        refreshToken: string;
        user: AuthUser;
      } | null>).detail;
      if (session?.accessToken && session.refreshToken && session.user) {
        useLandingAuth.getState().setSession(session);
      }
    };
    window.addEventListener("leadhold:auth-changed", syncSession);
    return () => window.removeEventListener("leadhold:auth-changed", syncSession);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!hydrated) {
        setStatus("loading");
        return;
      }

      setError(null);
      const handoff = searchParams.get("handoff");
      if (handoff) {
        if (exchangedHandoffRef.current === handoff) {
          if (useLandingAuth.getState().accessToken) {
            setStatus("authenticated");
          }
          return;
        }
        exchangedHandoffRef.current = handoff;
        try {
          await exchangeWebHandoff(handoff);
          const next = new URLSearchParams(searchParams);
          next.delete("handoff");
          setSearchParams(next, { replace: true });
          if (!cancelled) {
            setStatus("authenticated");
          }
          return;
        } catch (e) {
          exchangedHandoffRef.current = null;
          if (!cancelled) {
            setStatus("error");
            setError(
              e instanceof ApiError && e.code === "handoff_invalid"
                ? "Ссылка для входа устарела. Откройте оплату снова из мини-приложения."
                : "Не удалось войти по ссылке из приложения.",
            );
          }
          return;
        }
      }

      if (useLandingAuth.getState().accessToken) {
        try {
          await getMe();
          if (!cancelled) {
            setStatus("authenticated");
          }
          return;
        } catch {
          useLandingAuth.getState().clear();
        }
      }

      if (!cancelled) {
        setStatus("unauthenticated");
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [accessToken, hydrated, searchParams, setSearchParams]);

  return { status, error };
}
